import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import FacilityManagementService from "../../../services/FacilityManagementService";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: "16px 24px" },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    height: "auto",
    width: "850px",
    maxWidth: "none",
  },
}));

interface ViewUsersDialogProps {
  isOpen: boolean;
  dialogHeading?: string;
  handleClose: () => void;
  departmentName?: any;
  defaultDataForUser: any;
  user?: any;
}

const ViewUsersDialog = ({
  isOpen,
  departmentName,
  defaultDataForUser,
  handleClose,
  dialogHeading,
  user,
}: ViewUsersDialogProps) => {
  // Facilities & roles loaded via fetch-all (no fixed limit) so the label
  // lookups resolve even for users assigned beyond the first page.
  const [facilityAll, setFacilityAll] = useState<any>([]);
  const [rolesAll, setRolesAll] = useState<any>([]);

  const nameDepartment = defaultDataForUser?.departments?.map(
    (data: any) => data?.department?.name
  );
  const formate = nameDepartment.join(", ");
  const headerStyle = {
    fontWeight: "600",
    fontSize: "18px",
    paddingBottom: "10px",
  };

  // Page through ALL facilities/roles (mirrors IncidentReportTab) so the label
  // lookups are never truncated by a fixed page limit.
  const fetchAllFacilities = async () => {
    const first = await FacilityManagementService.getFacilityList({
      pageNumber: 1,
      limit: 100,
    });
    let rows: any[] = first?.data?.facilitys ?? [];
    const totalPages: number = first?.data?.totalPages ?? 1;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          FacilityManagementService.getFacilityList({
            pageNumber: i + 2,
            limit: 100,
          }).then((r: any) => r?.data?.facilitys ?? [])
        )
      );
      rows = rows.concat(...rest);
    }
    return rows;
  };

  const fetchAllRoles = async () => {
    const first = await FacilityManagementService.getRolesList({
      pageNumber: 1,
      limit: 100,
    });
    let rows: any[] = first?.data?.roles ?? [];
    const totalPages: number = first?.data?.totalPages ?? 1;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          FacilityManagementService.getRolesList({
            pageNumber: i + 2,
            limit: 100,
          }).then((r: any) => r?.data?.roles ?? [])
        )
      );
      rows = rows.concat(...rest);
    }
    return rows;
  };

  useEffect(() => {
    (async () => {
      const [facilities, roles] = await Promise.all([
        fetchAllFacilities(),
        fetchAllRoles(),
      ]);
      setFacilityAll(facilities);
      setRolesAll(roles);
    })();
  }, []);

  const roleData = rolesAll?.map((data: any) => ({
    label: data?.name,
    value: data?.id,
  }));
  const workLocationData = facilityAll?.map((data: any) => ({
    label: data?.name,
    value: data?.id,
  }));

  const findRoleById = (id: string | number) => {
    return roleData?.find((item: any) => item.value === id)?.label;
  };

  const findDepartmentById = (id: string | number) => {
    return workLocationData?.find((item: any) => item.value === id)?.label;
  };

  // const name = findNameById(targetId); // Replace targetId with the actual ID
  // console.log(name);
  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <div
          className="flex justify-between items-center rounded-lg"
          style={{ margin: "14px 24px" }}
        >
          <Heading title={dialogHeading ?? ""} customStyle={headerStyle} />
          <div onClick={handleClose} className="cursor-pointer">
            <CloseIcon />
          </div>
        </div>

        <DialogContent dividers>
          {defaultDataForUser?.id ? (
            <div
              style={{
                padding: "24px 16PX",
                display: "grid",
                gap: "24px 16px",
                gridTemplateColumns: "1fr 1fr",
                border: "1px solid #E8E8E8",
              }}
            >
              <p>
                <strong>BSA ID: </strong>
                {defaultDataForUser?.bsaId}
              </p>
              <p>
                <strong>{user} Name: </strong>
                {defaultDataForUser?.firstName} {defaultDataForUser?.lastName}
              </p>
              <p>
                <strong>Email: </strong> {defaultDataForUser?.email || "-"}
              </p>
              <p>
                <strong>Contact Number: </strong>{" "}
                {defaultDataForUser?.contactNumber || "-"}
              </p>
              <p>
                <strong>Role:</strong>{" "}
                {findRoleById(defaultDataForUser?.role) || "-"}
              </p>
              <p>
                <strong>Gender:</strong> {defaultDataForUser?.gender || "-"}
              </p>
              <p>
                <strong>Work Location:</strong>{" "}
                {findDepartmentById(departmentName) || "-"}
              </p>
              <p>
                <strong>Status:</strong> {defaultDataForUser?.status || "-"}
              </p>
              <p>
                <strong>Departments:</strong> {formate || "-"}
              </p>
            </div>
          ) : (
            <p>No data available</p>
          )}
        </DialogContent>

        <DialogActions>
          <div className="p-2">
            <Button
              label={TEXT_CONSTANTS?.CLOSE}
              variant="contained"
              onClickCb={handleClose}
              style={{
                width: "120px",
                height: "44px",
                padding: "12px 20px",
                fontSize: "16px",
                marginRight: "6px",
              }}
            />
          </div>
        </DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
};

export default ViewUsersDialog;
